import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import Loading from '../components/Loading';
import './Pages.css';

interface GamificationLevel {
  levelNumber: number;
  levelName: string;
  pointsRequired: number;
  benefitDescription?: string;
  isRedeemable: boolean;
}

interface GamificationRedemption {
  id: string;
  clientId: string;
  clientName: string;
  level: number;
  levelName: string;
  benefitDescription: string;
  redeemedAt: string;
  redeemedBy: string;
}

const levelEmojis: Record<string, string> = {
  'Starter': '🌱',
  'Bronze': '🥉',
  'Silver': '🥈',
  'Gold': '🥇',
  'Platinum': '💎',
  'Diamond': '💠',
  'Black': '⬛',
  'Infinity': '♾️',
  'Legend': '👑',
  'Founders': '🏆'
};

const Gamification: React.FC = () => {
  const [levels, setLevels] = useState<GamificationLevel[]>([]);
  const [redemptions, setRedemptions] = useState<GamificationRedemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'config' | 'redemptions'>('config');

  useEffect(() => {
    loadGamificationConfig();
    loadRedemptions();
  }, []);

  const loadGamificationConfig = async () => {
    try {
      const response = await api.get<GamificationLevel[]>('/admin/gamification');
      setLevels(response.data);
    } catch (error) {
      console.error('Error loading gamification config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRedemptions = async () => {
    try {
      const response = await api.get<GamificationRedemption[]>('/admin/gamification/redemptions');
      setRedemptions(response.data);
    } catch (error) {
      console.error('Error loading redemptions:', error);
    }
  };

  const handleLevelChange = (index: number, field: keyof GamificationLevel, value: any) => {
    const newLevels = [...levels];
    newLevels[index] = { ...newLevels[index], [field]: value };
    setLevels(newLevels);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Validate points are in ascending order
      for (let i = 1; i < levels.length; i++) {
        if (levels[i].pointsRequired <= levels[i - 1].pointsRequired) {
          alert('Os pontos devem estar em ordem crescente!');
          setSaving(false);
          return;
        }
      }

      await api.put('/admin/gamification', { levels });
      alert('Configuração de gamificação salva com sucesso!');
      await loadGamificationConfig();
    } catch (error: any) {
      console.error('Error saving gamification config:', error);
      alert(error.response?.data?.message || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Gamificação 🎮</h1>
        <p>Configure os níveis de gamificação e benefícios para seus clientes</p>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          Configuração
        </button>
        <button
          className={`tab ${activeTab === 'redemptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('redemptions')}
        >
          Resgates ({redemptions.length})
        </button>
      </div>

      {activeTab === 'config' && (
        <div className="gamification-config">
          <div className="info-box">
            <p>
              <strong>Como funciona:</strong> Os clientes acumulam pontos em todos os planos de fidelidade 
              do seu restaurante. Conforme atingem determinadas pontuações, eles sobem de nível. 
              Você pode oferecer benefícios opcionais para cada nível.
            </p>
          </div>

          <div className="levels-grid">
            {levels.map((level, index) => (
              <div key={level.levelNumber} className="level-card">
                <div className="level-header">
                  <span className="level-emoji">{levelEmojis[level.levelName]}</span>
                  <h3>
                    Nível {level.levelNumber}: {level.levelName}
                  </h3>
                </div>

                <div className="level-body">
                  <div className="form-group">
                    <label>Pontos necessários *</label>
                    <input
                      type="number"
                      min="0"
                      value={level.pointsRequired}
                      onChange={(e) =>
                        handleLevelChange(index, 'pointsRequired', parseInt(e.target.value) || 0)
                      }
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Descrição do benefício (opcional)</label>
                    <textarea
                      value={level.benefitDescription || ''}
                      onChange={(e) => handleLevelChange(index, 'benefitDescription', e.target.value)}
                      placeholder="Ex: 10% de desconto em todas as compras"
                      className="form-control"
                      rows={2}
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={level.isRedeemable}
                        onChange={(e) => handleLevelChange(index, 'isRedeemable', e.target.checked)}
                      />
                      <span>Benefício resgatável (requer registro manual)</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button
              className="btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Salvando...' : 'Salvar Configuração'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'redemptions' && (
        <div className="redemptions-list">
          {redemptions.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum resgate registrado ainda.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Cliente</th>
                    <th>Nível</th>
                    <th>Benefício</th>
                    <th>Registrado por</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((redemption) => (
                    <tr key={redemption.id}>
                      <td>{formatDate(redemption.redeemedAt)}</td>
                      <td>{redemption.clientName}</td>
                      <td>
                        <span className="level-badge">
                          {levelEmojis[redemption.levelName]} {redemption.levelName}
                        </span>
                      </td>
                      <td>{redemption.benefitDescription}</td>
                      <td>{redemption.redeemedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Gamification;




